import { memo, useEffect } from 'react'
import { Stack, Text } from 'ui/baseComponents'
import BrandedSectionHeader from 'ui/clinicianScreens/Patient/Report/components/BrandedSectionHeader'
import ReportSection from 'ui/clinicianScreens/Patient/Report/components/ReportSection'
import {
  REPORT_VARIANTS,
  SECTION_TITLES,
} from 'ui/clinicianScreens/Patient/Report/consts'
import { colors } from 'theme/colors'
import { useClinicalVisit } from 'ui/hooks'
import { EMAIL_SUPPORT } from 'ui/consts'

const STYLES = {
  column: {
    width: '50%',
  },
  concernContainer: {
    padding: '8px 0',
  },
  concernName: {
    width: '25%',
  },
  concernDiscussionPoints: {
    lineHeight: 'normal',
    width: '75%',
  },
  discussionPointsContainer: {
    padding: '8px 16px 24px 16px',
  },
  divider: {
    border: 'none',
    borderTop: `2px solid ${colors.COOL[400]}`,
  },
  sectionContainer: {
    backgroundColor: colors.COOL[50],
    borderRadius: '0 0 16px 16px',
  },
}

const ReportSectionTopConcerns = ({
  clinicalVisitId,
  onLoadStart = () => {},
  onLoadEnd = () => {},
  variant,
}) => {
  const title = SECTION_TITLES.TOP_CONCERNS
  const { data, hasError, loading } = useClinicalVisit({ clinicalVisitId })
  const { clinical_visit: clinicalVisit } = data || {}
  const { pre_visit_prep_response: preVisitPrepResponse } = clinicalVisit || {}
  const { other_notes: otherNotes, top_concerns: topConcerns } =
    preVisitPrepResponse || {}

  const leftColumn = topConcerns?.filter((_, index) => index % 2 === 0) || []
  const rightColumn = topConcerns?.filter((_, index) => index % 2 === 1) || []

  useEffect(() => {
    if (loading) {
      onLoadStart(title)
    } else {
      onLoadEnd(title)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  return (
    <ReportSection title={title} noBorder hidden={false} hideTitle>
      <Stack sx={STYLES.sectionContainer}>
        <BrandedSectionHeader
          {...{
            borderRadius: 'topOnly',
            showGuardianIcon: variant === REPORT_VARIANTS.guardian,
            title,
            variant,
          }}
        />
        {hasError && (
          <Stack sx={STYLES.discussionPointsContainer}>
            <Text variant="body16">
              {`There was an error retrieving your top concerns. Please try again
              or contact ${EMAIL_SUPPORT} if the issue persists.`}
            </Text>
          </Stack>
        )}
        {(otherNotes || topConcerns) && (
          <Stack sx={STYLES.discussionPointsContainer}>
            <Text variant="body14" color={colors.COOL[800]}>
              Discussion points:
            </Text>
            <hr style={STYLES.divider} />

            <Stack direction="row" spacing={4}>
              {[leftColumn, rightColumn].map((column, columnIndex) => (
                <Stack
                  key={columnIndex}
                  sx={STYLES.column}
                  data-cy={`top-concerns-column-${columnIndex}`}
                >
                  {column.map((concern) => (
                    <Stack key={concern.name} direction="column">
                      <Stack direction="row" sx={STYLES.concernContainer}>
                        <Text
                          variant="body16B"
                          sx={STYLES.concernName}
                          data-cy="concern-name"
                        >
                          {concern.name}
                        </Text>
                        <ul style={STYLES.concernDiscussionPoints}>
                          {concern.discussion_topics.map((topic) => (
                            <li key={topic}>
                              <Text variant="body16">{topic}</Text>
                            </li>
                          ))}
                        </ul>
                      </Stack>
                      <hr style={STYLES.divider} />
                    </Stack>
                  ))}
                </Stack>
              ))}
            </Stack>
            {otherNotes && (
              <Stack direction="column">
                <Text variant="body14">Notes:</Text>
                <Text variant="body16">{otherNotes}</Text>
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </ReportSection>
  )
}

export default memo(ReportSectionTopConcerns)
